"use client";

import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Mail } from "lucide-react";
import {
  subscribeToNewsletter,
  type NewsletterErrorCode,
} from "@/app/actions/newsletter";
import { NewsletterSchema } from "@/lib/schemas";

interface NewsletterFormProps {
  variant?: "default" | "compact";
  className?: string;
}

const NewsletterForm = ({ variant = "default", className = "" }: NewsletterFormProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const inputId = useId();

  const showError = (code: NewsletterErrorCode | undefined) => {
    toast({
      title: t("newsletter.errorTitle"),
      description: t(`newsletter.errors.${code ?? "unknown"}`, {
        defaultValue: t("newsletter.errors.unknown"),
      }),
      variant: "destructive",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = NewsletterSchema.safeParse({ email });
    if (!result.success) {
      showError("invalidEmail");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("email", email);

    const response = await subscribeToNewsletter(formData);

    setLoading(false);

    if (response.success) {
      setEmail("");
      toast({
        title: t("newsletter.successTitle"),
        description: t("newsletter.successDescription"),
      });
    } else {
      showError(response.error);
    }
  };

  const emailField = (
    <>
      <label htmlFor={inputId} className="sr-only">
        {t("newsletter.emailLabel")}
      </label>
      <Input
        id={inputId}
        type="email"
        name="email"
        autoComplete="email"
        required
        placeholder={t("newsletter.emailPlaceholder")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        maxLength={255}
        className="flex-1"
      />
    </>
  );

  if (variant === "compact") {
    return (
      <form onSubmit={handleSubmit} className={`flex flex-col sm:flex-row gap-2 ${className}`}>
        {emailField}
        <Button type="submit" variant="hero" size="default" disabled={loading}>
          {loading ? "..." : t("newsletter.subscribe")}
        </Button>
      </form>
    );
  }

  return (
    <div className={`bg-card border-2 border-border rounded-2xl p-8 ${className}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
          <Mail className="w-5 h-5 text-secondary" aria-hidden="true" />
        </div>
        <h3 className="font-heading font-bold text-xl">{t("newsletter.title")}</h3>
      </div>
      <p className="text-muted-foreground text-sm mb-5">
        {t("newsletter.description")}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
        {emailField}
        <Button type="submit" variant="hero" disabled={loading}>
          {loading ? t("newsletter.subscribing") : t("newsletter.subscribe")}
        </Button>
      </form>
    </div>
  );
};

export default NewsletterForm;
